/**
 * Words off a page of a book already on the shelf.
 *
 * The book arrives by its barcode ([ReadingList](ReadingList.tsx)) and this
 * is the second step: taking words out of a book whose identity is already
 * known, so the list they join is named after the real book.
 *
 * **Pasted, not photographed (27 Oct 2026).** This used to send a photo of
 * the page to Gemini, whose terms bar apps used by under-18s. Now the grown-up
 * copies the page's text with the phone's own reader (Live Text on an iPhone
 * or iPad, Google Lens on Android) and pastes it here. The photo never
 * reaches Word Sprout; the text is used for the suggestions and not kept.
 * research/page-scan-without-gemini/ has the reasoning.
 *
 * **Finding words adds nothing.** The page comes back as a list with tick
 * boxes, none ticked. A page is not consent to teach everything on it. A word
 * with several meanings asks which one the page means, because the app
 * cannot tell and must not guess.
 */
import { useState, type ReactNode } from 'react'
import { api, type Book, type PageChoice, type PageWord, type PageWords as Found } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button } from './ui'
import { fromServer, t, tn } from '../lib/i18n'

export function PageWords({
  learnerId, book, onAdded,
}: { learnerId: string; book: Book; onAdded: () => void }) {
  const [text, setText] = useState('')
  const [found, setFound] = useState<Found | null>(null)
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  // The meaning chosen for each word with several. None chosen yet: no tick.
  const [meaning, setMeaning] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [said, setSaid] = useState<string | null>(null)

  const find = async () => {
    if (!text.trim() || busy) return
    setBusy(true)
    setSaid(null)
    setFound(null)
    try {
      const out = await api.findBookPageWords(learnerId, book.book_id, text)
      setFound(out)
      setTicked(new Set())
      setMeaning({})
      if (!out.words.length) {
        setSaid(t('We found no words worth adding on that page. Try a page with more going on.'))
      }
    } catch (err) {
      setSaid(fromServer(err, 'That did not work.'))
    } finally {
      setBusy(false)
    }
  }

  const add = async () => {
    if (!ready || busy || !found) return
    setBusy(true)
    try {
      const choices: PageChoice[] = found.words
        .filter(w => ticked.has(w.word))
        .map(w => (w.senses.length > 1
          ? { word: w.word, word_sense_id: meaning[w.word] }
          : { word: w.word }))
      const out = await api.addFromBook(learnerId, book.title, choices, book.book_id)
      setSaid(out.added === 1 ? t('One word added.') : t('{n} words added.', { n: out.added }))
      startAgain()
      onAdded()
    } catch (err) {
      setSaid(fromServer(err, 'That did not work.'))
    } finally {
      setBusy(false)
    }
  }

  const startAgain = () => {
    setText('')
    setFound(null)
    setTicked(new Set())
    setMeaning({})
  }

  const toggle = (word: string) => setTicked((set) => {
    const next = new Set(set)
    if (next.has(word)) next.delete(word)
    else next.add(word)
    return next
  })

  const offerable = (found?.words ?? []).filter(w => !w.already_has && !w.blocked)
  const rest = (found?.words ?? []).filter(w => w.already_has || w.blocked)
  const needsMeaning = offerable.filter(
    w => ticked.has(w.word) && w.senses.length > 1 && !meaning[w.word])
  const ready = ticked.size > 0 && needsMeaning.length === 0

  return (
    <div style={{
      marginTop: 16, paddingTop: 16,
      borderTop: `1px solid ${theme.border}`,
    }}>
      <p style={{ margin: '0 0 10px', fontSize: 14, color: theme.faint,
                  lineHeight: 1.6 }}>
        {tn("Copy the words from one page of {title} with your phone's camera, then paste them here. You pick the ones worth learning.",
            { title: <b>{book.title}</b> })}
      </p>

      <HowTo title={t('How to copy on an iPhone or iPad')}>
        {t('Open the Camera and point it at the page. Tap the Live Text button (lines in a frame) at the bottom corner. Tap Select All, then Copy. Come back here and paste.')}
      </HowTo>
      <HowTo title={t('How to copy on Android')}>
        {t('Take a photo of the page and open it in Google Photos. Tap Lens, then Text, select the words and tap Copy. Google, not Word Sprout, reads the photo.')}
      </HowTo>

      {!found ? (
        <>
          <label style={{ display: 'block', margin: '12px 0 6px', fontSize: 14,
                          fontWeight: 700, color: theme.text }}
                 htmlFor={`page-text-${book.book_id}`}>
            {t('Text from one page')}
          </label>
          <textarea
            id={`page-text-${book.book_id}`}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            style={{
              width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
              fontSize: 15, lineHeight: 1.5, padding: 10, color: theme.text,
              border: `1px solid ${theme.border}`, borderRadius: radius.md,
            }}
          />
          <p style={{ margin: '6px 0 10px', fontSize: 13, color: theme.faint,
                      lineHeight: 1.5 }}>
            {t('Word Sprout gets the text you paste, never the photo. It is used to suggest words and is not kept.')}
          </p>
          <Button tone="quiet" disabled={busy || !text.trim()}
                  onClick={() => void find()}>
            {busy ? t('Finding words…') : t('Find words')}
          </Button>
        </>
      ) : null}

      {said ? (
        <p role="status" style={{ margin: '12px 0 0', fontSize: 15,
                                  color: theme.text2, lineHeight: 1.5 }}>
          {said}
        </p>
      ) : null}

      {found ? (
        <div style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {offerable.map(w => (
              <div key={w.word}>
                <WordTick word={w} on={ticked.has(w.word)}
                          onToggle={() => toggle(w.word)} />
                {ticked.has(w.word) && w.senses.length > 1 ? (
                  <Meanings word={w} chosen={meaning[w.word]}
                            onChoose={(id) => setMeaning(m => ({ ...m, [w.word]: id }))} />
                ) : null}
              </div>
            ))}
          </div>

          {rest.length ? (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, color: theme.fainter,
                            marginBottom: 6 }}>
                {t('Left out')}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {rest.map(w => (
                  <span key={w.word} style={{
                    fontSize: 13, color: theme.faint, background: theme.bg,
                    border: `1px solid ${theme.border}`,
                    borderRadius: radius.pill, padding: '5px 10px',
                  }}>
                    {w.word} · {w.blocked ? t('not for this app') : t('already has it')}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Button tone="child" disabled={!ready || busy}
                    onClick={() => void add()}>
              {!ticked.size ? t('Tick the ones you want')
                : needsMeaning.length ? t('Choose a meaning for each word')
                : ticked.size === 1 ? t('Add 1 word') : t('Add {n} words', { n: ticked.size })}
            </Button>
            <Button tone="quiet" disabled={busy} onClick={startAgain}>
              {t('Use a different page')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}


/** Copy steps, folded away until wanted. */
function HowTo({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details style={{ margin: '4px 0', fontSize: 14, color: theme.text2,
                      lineHeight: 1.55 }}>
      <summary style={{ cursor: 'pointer', fontWeight: 700, color: theme.text,
                        minHeight: 32 }}>
        {title}
      </summary>
      <p style={{ margin: '4px 0 8px' }}>{children}</p>
    </details>
  )
}


/** Which meaning the page uses, for a word the app knows several of. */
function Meanings({
  word, chosen, onChoose,
}: { word: PageWord; chosen?: string; onChoose: (id: string) => void }) {
  return (
    <fieldset style={{ margin: '6px 0 0 34px', padding: 0, border: 'none' }}>
      <legend style={{ fontSize: 14, fontWeight: 700, color: theme.text,
                       marginBottom: 4 }}>
        {t('Which meaning is used here?')}
      </legend>
      {word.senses.map(s => (
        <label key={s.word_sense_id} style={{
          display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0',
          fontSize: 15, color: theme.text2, lineHeight: 1.45, cursor: 'pointer',
        }}>
          <input type="radio" name={`meaning-${word.word}`}
                 checked={chosen === s.word_sense_id}
                 onChange={() => onChoose(s.word_sense_id)}
                 style={{ marginTop: 4 }} />
          <span>{s.definition}</span>
        </label>
      ))}
    </fieldset>
  )
}


/** One word off the page, with the sentence it came from. */
function WordTick({
  word, on, onToggle,
}: { word: PageWord; on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      style={{
        fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer',
        display: 'flex', gap: 12, alignItems: 'flex-start', width: '100%',
        padding: '12px 14px', minHeight: TAP_MIN,
        background: on ? theme.greenPale : '#FFFFFF',
        border: `2px solid ${on ? theme.green : theme.border}`,
        borderRadius: radius.md,
      }}
    >
      <span style={{
        flex: '0 0 auto', width: 22, height: 22, marginTop: 2,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, fontWeight: 800, color: '#FFFFFF',
        background: on ? theme.green : theme.border,
        borderRadius: radius.sm,
      }}>
        {on ? '✓' : ''}
      </span>
      <span>
        <span style={{ fontSize: 17, fontWeight: 800, color: theme.text }}>
          {word.word}
        </span>
        {word.senses.length > 1 ? (
          <span style={{ fontSize: 12, color: theme.greenDark, marginLeft: 8,
                         fontWeight: 700 }}>
            {t('{n} meanings', { n: word.senses.length })}

          </span>
        ) : word.definition ? (
          <span style={{ display: 'block', fontSize: 15, color: theme.text2,
                         marginTop: 3, lineHeight: 1.45 }}>
            {word.definition}
          </span>
        ) : null}
        {word.sentence ? (
          <span style={{ display: 'block', fontSize: 14, color: theme.muted,
                         marginTop: 3, fontStyle: 'italic', lineHeight: 1.45 }}>
            “{word.sentence}”
          </span>
        ) : null}
      </span>
    </button>
  )
}
